// Where the browser is allowed to go after authentication.
//
// Everything here exists because `?next=` is attacker-controlled: it arrives
// in the URL of a page we hand to a logged-in user, and two of the things we
// do with it — navigating, and appending a one-time OAuth nav nonce — are
// exactly what an attacker wants pointed at their own host.

// The WHATWG URL parser strips ASCII TAB, LF and CR *before* parsing, so a
// prefix check on the raw string does not see what the browser will see:
// "/<TAB>/evil.tld/x" reads as "//evil.tld/x" and resolves to another origin
// while passing any "does it start with a single slash" test. Reject those
// bytes outright rather than trying to predict the parser, and reject the
// rest of the C0 range and DEL with them — none of it belongs in a path we
// generated.
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/

/**
 * Validate an attacker-controlled `?next=` into a same-origin relative path.
 *
 * Returns the original string when it is safe to navigate to, or null. The
 * prefix checks stay (they are cheap and they document the intent), but the
 * decision is made by the same parser the browser uses, comparing origins —
 * a string test alone has never been enough here.
 */
export function safeNext(value) {
    if (typeof value !== "string" || !value) return null
    if (CONTROL_CHARS.test(value)) return null
    if (!value.startsWith("/")) return null
    if (value.startsWith("//")) return null
    if (value.startsWith("/\\")) return null
    try {
        if (new URL(value, window.location.origin).origin !== window.location.origin) return null
    } catch (e) {
        // Unparseable is not navigable.
        return null
    }
    return value
}

// Device-bound sessions leave exactly one door a top-level navigation still
// has to walk through: `GET /oauth/authorize`. The device key lives in
// localStorage and a navigation cannot carry a header, so the backend asks
// for proof of a different shape — a one-time `nav` nonce minted by
// `POST /oauth/nav`, which IS an XHR and therefore does go through the
// device-key check. Without a valid nonce /oauth/authorize answers exactly
// as it does for a logged-out visitor: 302 to /login?next=<authorize URL>.
//
// So every place that sends the browser to `next` after authentication has
// to mint one first. The nonce is good once and for 60 seconds: mint it
// immediately before navigating, never in advance, never twice.
//
// The nonce is a bearer credential for one navigation: whoever presents it
// first, together with the session cookie, reaches consent. So the one thing
// that must never happen is minting it for a URL that points somewhere else
// — hence the origin check here as well as in safeNext, and the
// relative-only navigation below.
const AUTHORIZE_PATH = "/oauth/authorize"

export function needsNavNonce(next) {
    if (typeof next !== "string" || CONTROL_CHARS.test(next) || !next.startsWith("/")) return false
    try {
        const url = new URL(next, window.location.origin)
        // Origin first: a `next` that resolves elsewhere must never be given
        // a nonce, whatever its path spells. Then the parsed pathname, not a
        // prefix — `/oauth/authorized-evil` is not the consent endpoint.
        // Exact match, so `/oauth/authorize/` deliberately does NOT count:
        // the FastAPI route is exact as well, and a trailing slash there is a
        // 404, not consent. A `next` shaped like that is a bug or a probe;
        // either way it gets the plain navigation and no nonce.
        return url.origin === window.location.origin && url.pathname === AUTHORIZE_PATH
    } catch (e) {
        return false
    }
}

/**
 * One authenticated round trip before we ask for a nonce.
 *
 * Unconditional, and that is the whole point — "only when localStorage is
 * empty" is the bug this replaced. A key in storage says nothing about the
 * session we are holding right now: it may belong to a previous one (the
 * cookie expired without a logout, an admin `login_as`, a shared machine).
 * Worse, `Login.vue` emits `context:changed` and calls `redirectAfterAuth`
 * from a `setTimeout`, so the redirect races the very refetch that would have
 * replaced that key. Minting with a stale key against a session the backend
 * has not bound yet is a straight 401 from `/oauth/nav` (already-bound plus
 * matching key, no grace there) — an error where the user expected consent.
 *
 * Awaiting `/context` first fixes both halves at once: it is the request that
 * binds the session, and its response carries the key, which the interceptor
 * writes over whatever was there (rule 1 in deviceKey.js).
 *
 * Never rejects, and deliberately does not short-circuit when the key is
 * still missing afterwards. Two different backends answer that way and only
 * one is a problem:
 *
 *  - a backend that predates device-bound sessions issues no keys at all and
 *    has no `/oauth/nav` either, so the mint 404s and the caller falls back
 *    to a plain navigation — refusing locally here would break consent for
 *    that pairing, which the 404 fallback exists to protect;
 *  - a backend that does bind sessions answers the mint with 401, which the
 *    caller surfaces as an error without navigating.
 *
 * Letting the server decide keeps both behaviours correct instead of
 * guessing from the absence of a string in localStorage.
 */
function ensureDeviceKey(services) {
    const settled = () => undefined
    return services.Context().get().then(settled, settled)
}

/**
 * Send the browser to an already-validated `next`.
 *
 * Resolves once the navigation has been started (the page is on its way out).
 * Rejects — without navigating — when the nonce could not be minted, so the
 * caller can show the error instead of bouncing the user through a login
 * loop with no explanation. The rejection has the `{body, status}` shape the
 * services layer produces, which is what `displayErrors` expects.
 */
export function navigateToNext(services, next) {
    // Re-parse, and navigate to path+query+hash only. Two reasons, both worth
    // the extra lines: an absolute URL can never be produced here, so even a
    // regression in the guards above cannot send the nonce to another host;
    // and `searchParams.set` puts `nav` in the query where it belongs, while
    // string concatenation would append it *after* a `#fragment`, where the
    // server never sees it — a silent bounce back to /login.
    let url
    try {
        url = new URL(next, window.location.origin)
    } catch (e) {
        return Promise.reject({status: 400, body: {detail: "Invalid redirect target"}})
    }
    if (url.origin !== window.location.origin) {
        // safeNext should have refused this long before we got here. If it
        // did not, stop — do not navigate off-origin, and above all do not
        // mint a nonce for it.
        return Promise.reject({status: 400, body: {detail: "Invalid redirect target"}})
    }
    const go = () => window.location.assign(url.pathname + url.search + url.hash)

    if (!needsNavNonce(next)) {
        go()
        return Promise.resolve()
    }
    // `POST /oauth/nav` refuses outright when the session is not yet bound or
    // the request carries no device key — no grace leniency on the mint, by
    // design: the nonce is what turns a navigation into an authenticated one,
    // so it may only be issued to someone who has already proven the device.
    //
    // So always make one authenticated round trip first and let the response
    // interceptor store what comes back — never skip it on the strength of a
    // key already sitting in localStorage, which may well belong to the
    // previous session (see ensureDeviceKey). `/context` is the natural
    // choice: cheap, uncached, and the same call every page load makes.
    return ensureDeviceKey(services).then(() => services.OAuthNav().save()).then(
        resp => {
            const nonce = resp && resp.body && resp.body.nav
            if (!nonce) {
                return Promise.reject({
                    status: 502,
                    body: {detail: "Could not start the authorization flow, please try again"}
                })
            }
            url.searchParams.set("nav", nonce)
            go()
        },
        err => {
            // 404 = a backend older than the nav nonce, which still lets the
            // cookie alone through /oauth/authorize. Frontends ship before or
            // with lm (the device-key rollout requires that order), so this
            // pairing is expected for a while and must not break consent.
            if (err && err.status === 404) {
                go()
                return
            }
            return Promise.reject(err)
        }
    )
}
